import {
  FieldConfigurationType,
  type FieldConfiguration,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  GroupingField,
  ReportField,
  SortableField,
  SortConfiguration,
  SortField,
} from "../../../../../../../../api/types/svTypes.ts";
import { Ordering, SortType } from "../../../../../../../../api/types/svTypes.ts";
import { isCustomColumn, isDimensionBased, isMeasure } from "../../../../utilities/fieldsHelper.ts";
import { isSortable } from "../fields/custom-field/utilities/calculationTypeCapabilities.ts";

export const setGroupingFlag = (fields: FieldConfiguration[], groups: GroupingField[], sorts: SortConfiguration[]) => {
  const groupIds = new Set(groups.map((g) => g.id));
  const copy = structuredClone(sorts);

  copy.forEach((sorting) => {
    const field = fields.find((f) => f.guid === sorting.id);
    if (field && groupIds.has(field.guid)) {
      sorting.isGroupField = true;
    }
  });

  return copy;
};

export const addMissingGroupSorts = (
  fields: FieldConfiguration[],
  groups: GroupingField[],
  sorts: SortConfiguration[]
) => {
  const groupIds = new Set(groups.map((g) => g.id));
  const sortMetaNames = new Set(sorts.map((s) => s.id));

  const missingSorts = fields
    .filter((field) => groupIds.has(field.guid) && !sortMetaNames.has(field.guid))
    .map(
      (field) =>
        ({
          name: field.name,
          isGroupField: true,
          ordering: Ordering.Ascending,
          caption: field.customLabel ?? field.name,
          id: field.guid,

          type:
            field.type === FieldConfigurationType.Dimension
              ? SortType.Dimension
              : field.type === FieldConfigurationType.Custom
                ? SortType.Custom
                : SortType.Measure,
        }) as SortConfiguration
    );

  return [...structuredClone(sorts), ...missingSorts];
};

export const sortSortingFields = (sorts: SortField[], groupMetaNames: string[]) => {
  const groupedFields = sorts.filter((field) => field.config.isGroupField);
  const regularFields = sorts.filter((field) => !field.config.isGroupField);

  groupedFields.sort(
    (a, b) => groupMetaNames.findIndex((g) => g === a.meta.name) - groupMetaNames.findIndex((g) => g === b.meta.name)
  );

  return [...groupedFields, ...regularFields];
};

export const getAvailableSortFields = (values: ReportField[], sortingAreaValues: SortField[]): SortableField[] => {
  return values
    .filter(
      (f): f is SortableField =>
        isDimensionBased(f) || isMeasure(f) || (isCustomColumn(f) && isSortable(f.config.calculationType))
    )
    .filter((f) => !sortingAreaValues.find((v) => v.config.id === f.config.guid));
};

export const getGroupMetaNames = (grouping: GroupingField[], fields: ReportField[]) => {
  return grouping.map((g) => fields.find((f) => f.config.guid === g.id)?.meta?.name ?? "");
};

export const getSortType = (field: ReportField): SortType => {
  if (isMeasure(field)) {
    return SortType.Measure;
  }
  if (isCustomColumn(field)) {
    return SortType.Custom;
  }
  if (isDimensionBased(field)) {
    return SortType.Dimension;
  }
  return SortType.Dimension;
};
